import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { fmtINR, fmtINRCompact } from "@/homeiq/services/format";
import { BANK_FOIR_BANDS, BANK_DISCLAIMER } from "@/homeiq/services/placeholders";
import { emi as emiFor } from "@/homeiq/engine/affordabilityCore";

const EASE = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay, ease: EASE }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

function SectionHead({ eyebrow, title, sub }) {
    return (
        <div className="mb-2">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold mb-2">— {eyebrow}</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0A1F44] tracking-tight">{title}</h2>
            {sub && <p className="mt-3 text-[#0A1F44]/65 max-w-2xl text-sm md:text-base">{sub}</p>}
        </div>
    );
}

function PillarHead({ index, title, sub }) {
    return (
        <div className="flex items-baseline gap-3 mb-4">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold tabular-nums">
                {String(index).padStart(2, "0")}
            </span>
            <div>
                <h3 className="font-display font-extrabold text-2xl text-[#0A1F44]">{title}</h3>
                {sub && <p className="text-sm text-[#0A1F44]/65 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section A — Context strip (Affordability + Market handoff)
// ─────────────────────────────────────────────────────────────────────
function ContextStrip() {
    const {
        affordableBudget, eligibleLoan, placeholderEMI,
        rate, tenure, downPayment, sba,
        spotlightMarket, setActiveSection,
    } = useHomeIQ();

    // Dynamic home value — spotlight market wins, else affordableBudget.
    const homeValue = spotlightMarket?.propertyCost ?? affordableBudget;
    const sourceLabel = spotlightMarket
        ? `${spotlightMarket.area} · ${sba} sqft`
        : "Your affordability budget";

    const tiles = [
        { k: "Home value", v: fmtINR(homeValue), sub: sourceLabel },
        { k: "Eligible loan", v: fmtINR(eligibleLoan), sub: "from your FOIR · live" },
        { k: "Monthly EMI", v: `₹${placeholderEMI.toLocaleString("en-IN")}`, sub: "principal + interest" },
        { k: "Interest rate", v: `${rate.toFixed(2)}%`, sub: "from your loan inputs" },
        { k: "Tenure", v: `${tenure} years`, sub: "from your loan inputs" },
        { k: "Down payment", v: fmtINR(downPayment), sub: "from your loan inputs" },
    ];

    return (
        <Reveal>
            <div data-testid="loan-context-strip" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-5 md:p-6">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
                    <div className="flex items-baseline gap-2">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#1E6BFF] font-semibold">— Source</span>
                        <span className="text-sm text-[#0A1F44]">
                            {spotlightMarket ? "Loan modelled against your selected market" : "Loan modelled against your affordability budget"}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-[11px]">
                        <button
                            type="button"
                            onClick={() => setActiveSection("affordability")}
                            data-testid="loan-link-affordability"
                            className="font-semibold text-[#1E6BFF] hover:text-[#0A1F44] underline-offset-2 hover:underline transition-colors"
                        >
                            Edit Affordability →
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveSection("market")}
                            data-testid="loan-link-market"
                            className="font-semibold text-[#1E6BFF] hover:text-[#0A1F44] underline-offset-2 hover:underline transition-colors"
                        >
                            {spotlightMarket ? "Change market →" : "Pick a market →"}
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {tiles.map((t) => (
                        <div key={t.k} data-testid={`loan-ctx-${t.k.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-2xl bg-[#F7F8FB] p-3">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">{t.k}</div>
                            <div className="mt-1 font-display font-extrabold text-lg text-[#0A1F44] tabular-nums">{t.v}</div>
                            <div className="text-[10px] text-[#0A1F44]/50 mt-0.5 leading-tight">{t.sub}</div>
                        </div>
                    ))}
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section B — Interactive sliders (retained, ranges aligned with Affordability)
// ─────────────────────────────────────────────────────────────────────
function LoanInputs() {
    const { downPayment, setDownPayment, tenure, setTenure, rate, setRate } = useHomeIQ();
    return (
        <Reveal>
            <div data-testid="loan-inputs" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold mb-4">— Model the loan</div>
                <div className="grid md:grid-cols-3 gap-6">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold">Down payment</div>
                        <div className="mt-1 font-display font-extrabold text-3xl text-[#0A1F44]">{fmtINR(downPayment)}</div>
                        <input
                            type="range"
                            min={500000}
                            max={50000000}
                            step={100000}
                            value={downPayment}
                            onChange={(e) => setDownPayment(Number(e.target.value))}
                            data-testid="loan-down-slider"
                            className="mt-3 w-full accent-[#1E6BFF]"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold">Tenure</div>
                        <div className="mt-1 font-display font-extrabold text-3xl text-[#0A1F44]">{tenure} <span className="text-2xl text-[#0A1F44]/50">years</span></div>
                        <input
                            type="range"
                            min={5}
                            max={30}
                            step={1}
                            value={tenure}
                            onChange={(e) => setTenure(Number(e.target.value))}
                            data-testid="loan-tenure-slider"
                            className="mt-3 w-full accent-[#1E6BFF]"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold">Interest rate</div>
                        <div className="mt-1 font-display font-extrabold text-3xl text-[#0A1F44]">{rate.toFixed(2)}<span className="text-2xl text-[#0A1F44]/50">%</span></div>
                        <input
                            type="range"
                            min={6.5}
                            max={12}
                            step={0.1}
                            value={rate}
                            onChange={(e) => setRate(Number(e.target.value))}
                            data-testid="loan-rate-slider"
                            className="mt-3 w-full accent-[#1E6BFF]"
                        />
                    </div>
                </div>
                <div className="mt-5 text-[11px] text-[#0A1F44]/55">
                    Sliders are shared with the Affordability tab — changing them here updates your full plan.
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section C — Summary card (live: principal/interest/total from loanOutputs)
// ─────────────────────────────────────────────────────────────────────
function Summary() {
    const { loanOutputs } = useHomeIQ();
    const emi = Math.round(loanOutputs.emi);
    const stats = [
        { k: "Monthly EMI", v: `₹${emi.toLocaleString("en-IN")}` },
        { k: "Principal", v: fmtINR(loanOutputs.total_principal) },
        { k: "Total interest", v: fmtINR(loanOutputs.total_interest) },
        { k: "Total outflow", v: fmtINR(loanOutputs.total_outflow) },
    ];
    const principalPct = loanOutputs.principal_pct;
    const interestPct = loanOutputs.interest_pct;
    return (
        <Reveal delay={0.05}>
            <div data-testid="loan-summary" className="rounded-3xl bg-[#0A1F44] text-white p-6 md:p-10 relative overflow-hidden">
                <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 relative">
                    {stats.map((s) => (
                        <div key={s.k}>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">{s.k}</div>
                            <div className="mt-2 font-display font-black text-2xl md:text-3xl lg:text-4xl tracking-tight">{s.v}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-6 relative">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold mb-2">Principal vs interest split</div>
                    <div className="h-3 rounded-full overflow-hidden flex">
                        <div className="h-full bg-[#1E6BFF]" style={{ width: `${principalPct}%` }} />
                        <div className="h-full bg-white/30" style={{ width: `${interestPct}%` }} />
                    </div>
                    <div className="mt-2 flex justify-between text-xs text-white/55">
                        <span>Principal {principalPct}%</span>
                        <span>Interest {interestPct}%</span>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section D — Equity Milestone Rings (LIVE · derived from amortisation)
// ─────────────────────────────────────────────────────────────────────
const MILESTONE_TONES = { 25: "#dc2626", 50: "#d97706", 75: "#1E6BFF", 100: "#16a34a" };

function MilestoneRing({ pct, label, tone, delay }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay, ease: EASE }}
            data-testid={`loan-milestone-${pct}`}
            className="rounded-2xl border border-[#0A1F44]/10 bg-white p-5 flex flex-col items-center text-center"
        >
            <div
                className="relative w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center"
                style={{ background: `conic-gradient(${tone} ${pct * 3.6}deg, #E5E9F2 0deg)` }}
            >
                <div className="absolute inset-2 rounded-full bg-white flex flex-col items-center justify-center">
                    <span className="font-display font-extrabold text-2xl text-[#0A1F44] tabular-nums">{pct}<span className="text-sm">%</span></span>
                    <span className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/50 font-semibold">owned</span>
                </div>
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Reached at</div>
            <div className="font-display font-extrabold text-base md:text-lg text-[#0A1F44] tabular-nums">{label}</div>
        </motion.div>
    );
}

function EquityMilestones() {
    const { loanOutputs } = useHomeIQ();
    return (
        <Reveal>
            <div data-testid="loan-equity-milestones" className="space-y-4">
                <PillarHead index={1} title="When you actually own your home" sub="Each ring marks when your repayments cross an equity threshold." />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {loanOutputs.equity_milestones.map((m, i) => (
                        <MilestoneRing
                            key={m.pct}
                            pct={m.pct}
                            label={m.label}
                            tone={MILESTONE_TONES[m.pct]}
                            delay={0.05 + i * 0.08}
                        />
                    ))}
                </div>
                <p className="text-[11px] text-[#0A1F44]/55">
                    Live · simulated month-by-month from your EMI, rate and tenure.
                </p>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section E — Prepayment Simulator (LIVE · shared state across Affordability + Loan)
// ─────────────────────────────────────────────────────────────────────
function PrepaymentSimulator() {
    const {
        partPaymentLump, setPartPaymentLump,
        partPaymentFrequency, setPartPaymentFrequency,
        loanOutputs,
    } = useHomeIQ();
    const { tenure_cut_months, interest_saved, new_tenure_months } = loanOutputs.prepayment;
    const cutYrs = (tenure_cut_months / 12).toFixed(1);
    const newTenureYrs = (new_tenure_months / 12).toFixed(1);
    // New end date — N months from today.
    const endDateLabel = (() => {
        const d = new Date();
        d.setMonth(d.getMonth() + new_tenure_months);
        return d.toLocaleString("en-IN", { month: "short", year: "numeric" });
    })();
    const tiles = [
        { k: "Tenure cut", v: `${cutYrs} yrs`, sub: `from ${loanOutputs.tenure} → ${newTenureYrs} yrs` },
        { k: "Interest saved", v: fmtINRCompact(interest_saved), sub: "vs no prepayment" },
        { k: "New end date", v: endDateLabel, sub: "from today" },
        { k: "Lump", v: fmtINRCompact(partPaymentLump), sub: partPaymentFrequency.toLowerCase() },
    ];
    return (
        <Reveal>
            <div id="loan-prepayment-simulator" data-testid="loan-prepayment-simulator" className="space-y-4 scroll-mt-32">
                <PillarHead index={2} title="Prepayment simulator" sub="Add a lump sum — watch tenure and interest shrink." />
                <div className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold">Extra prepayment</div>
                            <div className="mt-1 font-display font-extrabold text-3xl text-[#0A1F44]">{fmtINR(partPaymentLump)}</div>
                            <input
                                type="range"
                                min={0}
                                max={1000000}
                                step={10000}
                                value={partPaymentLump}
                                onChange={(e) => setPartPaymentLump(Number(e.target.value))}
                                data-testid="loan-prepay-slider"
                                className="mt-3 w-full accent-[#1E6BFF]"
                            />
                            <div className="mt-4 flex gap-2 flex-wrap">
                                {["One-time", "Annual", "Monthly extra"].map((f) => (
                                    <button
                                        key={f}
                                        type="button"
                                        onClick={() => setPartPaymentFrequency(f)}
                                        data-testid={`loan-prepay-freq-${f.replace(/\s+/g, "-").toLowerCase()}`}
                                        className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                                            partPaymentFrequency === f
                                                ? "bg-[#0A1F44] text-white"
                                                : "bg-[#0A1F44]/6 text-[#0A1F44]/70 hover:bg-[#0A1F44]/10"
                                        }`}
                                    >
                                        {f}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {tiles.map((t) => (
                                <div key={t.k} data-testid={`loan-prepay-tile-${t.k.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-2xl bg-[#F7F8FB] p-3">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">{t.k}</div>
                                    <div className="mt-1 font-display font-extrabold text-xl text-[#0A1F44] tabular-nums">{t.v}</div>
                                    <div className="text-[10px] text-[#0A1F44]/50">{t.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-4 text-[11px] text-[#0A1F44]/55">Live · re-simulates amortisation as you change lump or frequency.</div>
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section F — Rate Sensitivity (LIVE · ±50 bps around user's current rate)
// ─────────────────────────────────────────────────────────────────────
function RateSensitivity() {
    const { loanOutputs, affordabilityOutputs, rate, tenure } = useHomeIQ();
    const principal = affordabilityOutputs.eligible_loan;
    const baselineInterest = loanOutputs.total_interest;
    const baselineEMI = loanOutputs.emi;
    const ladder = useMemo(() => {
        const deltas = [-50, -25, 0, 25, 50]; // bps
        return deltas.map((delta) => {
            const r = rate + delta / 100;
            const e = emiFor(principal, r, tenure);
            const totalInterest = Math.max(0, e * tenure * 12 - principal);
            let status = "current";
            if (delta < 0) status = "save";
            else if (delta > 0) status = "cost";
            return { delta, rate: r, emi: e, totalInterest, status };
        });
    }, [principal, rate, tenure]);
    return (
        <Reveal>
            <div data-testid="loan-rate-sensitivity" className="space-y-4">
                <PillarHead index={3} title="Rate sensitivity" sub="What 50 bps in either direction does to your repayment." />
                <div className="rounded-3xl border border-[#0A1F44]/10 bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[#0A1F44]/4 text-[#0A1F44]/65 text-[11px] uppercase tracking-[0.2em]">
                            <tr>
                                <th className="text-left px-6 py-3 font-semibold">Scenario</th>
                                <th className="text-right px-4 py-3 font-semibold">Rate</th>
                                <th className="text-right px-4 py-3 font-semibold">EMI</th>
                                <th className="text-right px-6 py-3 font-semibold">Total interest</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ladder.map((r) => {
                                const tone = r.status === "current" ? "#1E6BFF" : r.status === "save" ? "#16a34a" : "#dc2626";
                                const label = r.status === "current" ? "Today" : `${r.delta > 0 ? "+" : ""}${r.delta} bps`;
                                const interestDelta = r.totalInterest - baselineInterest;
                                const interestDeltaLabel = r.status === "current"
                                    ? ""
                                    : ` (${interestDelta >= 0 ? "+" : "−"}${fmtINRCompact(Math.abs(interestDelta))})`;
                                const emiDelta = Math.round(r.emi - baselineEMI);
                                return (
                                    <tr key={r.delta} data-testid={`loan-rate-row-${r.delta}`} className={`border-t border-[#0A1F44]/8 ${r.status === "current" ? "bg-[#1E6BFF]/4" : ""}`}>
                                        <td className="px-6 py-3 font-semibold" style={{ color: tone }}>{label}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-[#0A1F44]">{r.rate.toFixed(2)}%</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-[#0A1F44]">
                                            ₹{Math.round(r.emi).toLocaleString("en-IN")}
                                            {r.status !== "current" && (
                                                <span className="text-[10px] text-[#0A1F44]/55 ml-1">
                                                    ({emiDelta >= 0 ? "+" : "−"}₹{Math.abs(emiDelta).toLocaleString("en-IN")})
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-3 text-right tabular-nums text-[#0A1F44]">
                                            {fmtINRCompact(r.totalInterest)}
                                            {interestDeltaLabel && (
                                                <span className="text-[10px] text-[#0A1F44]/55 ml-1">{interestDeltaLabel}</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p className="text-[11px] text-[#0A1F44]/55">Live · re-computed around your current rate as you slide.</p>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section G — Bank Comparison (LIVE · per-bank EMI from each rate)
// ─────────────────────────────────────────────────────────────────────
const GODREJ_HINT = "Did you know? Godrej Finance offers Design Your EMI structures where repayments can be customised based on expected income growth.";

function BankComparison() {
    const { affordabilityOutputs, tenure } = useHomeIQ();
    const principal = affordabilityOutputs.eligible_loan;
    const banks = useMemo(() => {
        return BANK_FOIR_BANDS.map((b) => {
            const e = emiFor(principal, b.rate, tenure);
            return {
                ...b,
                emi: e,
                totalInterest: Math.max(0, e * tenure * 12 - principal),
            };
        });
    }, [principal, tenure]);
    return (
        <Reveal>
            <div data-testid="loan-bank-comparison" className="space-y-4">
                <PillarHead index={4} title="Bank-by-bank" sub="Same loan, six lenders. What you pay where." />
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {banks.map((b) => {
                        const isGodrej = /godrej/i.test(b.name);
                        return (
                            <div
                                key={b.name}
                                data-testid={`loan-bank-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className="rounded-2xl border border-[#0A1F44]/10 bg-white p-4"
                            >
                                <div className="flex items-baseline justify-between">
                                    <div className="font-display font-extrabold text-lg text-[#0A1F44]">{b.name}</div>
                                    <div className="text-[11px] text-[#0A1F44]/55 tabular-nums">{b.rate.toFixed(2)}% p.a.</div>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-2">
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">EMI</div>
                                        <div className="font-display font-extrabold text-base text-[#0A1F44] tabular-nums">₹{Math.round(b.emi).toLocaleString("en-IN")}</div>
                                    </div>
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.2em] text-[#0A1F44]/45 font-semibold">Total interest</div>
                                        <div className="font-display font-extrabold text-base text-[#0A1F44] tabular-nums">{fmtINRCompact(b.totalInterest)}</div>
                                    </div>
                                </div>
                                {isGodrej && (
                                    <p
                                        data-testid="loan-bank-godrej-hint"
                                        className="mt-3 pt-3 border-t border-[#0A1F44]/8 text-[10px] text-[#1E6BFF] leading-snug"
                                    >
                                        {GODREJ_HINT}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
                <p data-testid="loan-bank-disclaimer" className="text-[11px] text-[#0A1F44]/55 italic">{BANK_DISCLAIMER}</p>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section H — Effective EMI vs Current Rent (NEW · placeholder)
// ─────────────────────────────────────────────────────────────────────
function EffectiveEMIvsRent() {
    const { rent, placeholderEMI } = useHomeIQ();
    // Placeholder net-of-tax effective EMI (Phase 2: derive from Sec 24(b) + 80C).
    const taxSavingPerMonth = 16700;
    const effectiveEMI = placeholderEMI - taxSavingPerMonth;
    const hasRent = rent != null && rent > 0;
    const delta = hasRent ? effectiveEMI - rent : 0;
    return (
        <Reveal>
            <div data-testid="loan-effective-emi-vs-rent" className="space-y-4">
                <PillarHead index={5} title="Effective EMI vs your current rent" sub="EMI after tax savings, side-by-side with what you pay today." />
                <div className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="rounded-2xl bg-[#F7F8FB] p-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Headline EMI</div>
                            <div className="mt-2 font-display font-extrabold text-3xl text-[#0A1F44] tabular-nums">₹{placeholderEMI.toLocaleString("en-IN")}</div>
                            <div className="text-[11px] text-[#0A1F44]/50">monthly</div>
                        </div>
                        <div className="rounded-2xl bg-[#0A1F44]/4 p-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Tax savings</div>
                            <div className="mt-2 font-display font-extrabold text-3xl text-[#16a34a] tabular-nums">− ₹{taxSavingPerMonth.toLocaleString("en-IN")}</div>
                            <div className="text-[11px] text-[#0A1F44]/50">Sec 24(b) + 80C · placeholder</div>
                        </div>
                        <div className="rounded-2xl bg-[#1E6BFF]/8 p-4 border border-[#1E6BFF]/30">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1E6BFF] font-semibold">Effective EMI</div>
                            <div className="mt-2 font-display font-extrabold text-3xl text-[#0A1F44] tabular-nums">₹{effectiveEMI.toLocaleString("en-IN")}</div>
                            <div className="text-[11px] text-[#0A1F44]/50">after tax savings</div>
                        </div>
                    </div>
                    {hasRent ? (
                        <div className="mt-5 rounded-2xl bg-[#0A1F44] text-white p-5 flex items-center justify-between flex-wrap gap-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">vs Your current rent</div>
                                <div className="mt-1 font-display font-extrabold text-2xl">
                                    ₹{rent.toLocaleString("en-IN")} <span className="text-white/55 text-base">/ mo</span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Difference</div>
                                <div className={`mt-1 font-display font-extrabold text-2xl tabular-nums ${delta >= 0 ? "text-[#7FA8FF]" : "text-[#16a34a]"}`}>
                                    {delta >= 0 ? "+" : ""}₹{Math.abs(delta).toLocaleString("en-IN")}
                                </div>
                                <div className="text-[11px] text-white/55">{delta >= 0 ? "more than today's rent" : "less than today's rent"}</div>
                            </div>
                        </div>
                    ) : (
                        <div data-testid="loan-rent-prompt" className="mt-5 rounded-2xl bg-[#F7F8FB] border border-dashed border-[#0A1F44]/15 p-5 text-sm text-[#0A1F44]/65 italic">
                            Enter your current monthly rent on the home screen to personalise projections.
                        </div>
                    )}
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section I — Amortisation schedule (LIVE · 5y default, expand to full)
// ─────────────────────────────────────────────────────────────────────
function Schedule() {
    const [expanded, setExpanded] = useState(false);
    const { loanOutputs } = useHomeIQ();
    const fullSchedule = loanOutputs.schedule;
    const visible = expanded ? fullSchedule : fullSchedule.slice(0, 5);
    return (
        <Reveal>
            <div data-testid="loan-schedule" className="space-y-4">
                <PillarHead index={6} title="Year-by-year repayment" sub="How principal eats into interest over time." />
                <div className="rounded-3xl border border-[#0A1F44]/10 bg-white overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#0A1F44]/4 text-[#0A1F44]/65 text-[11px] uppercase tracking-[0.2em]">
                                <tr>
                                    <th className="text-left px-6 md:px-8 py-3 font-semibold">Year</th>
                                    <th className="text-right px-4 py-3 font-semibold">Principal</th>
                                    <th className="text-right px-4 py-3 font-semibold">Interest</th>
                                    <th className="text-right px-4 py-3 font-semibold">Total paid</th>
                                    <th className="text-right px-6 md:px-8 py-3 font-semibold">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((r) => (
                                    <tr key={r.year} data-testid={`loan-schedule-row-${r.year}`} className="border-t border-[#0A1F44]/8 hover:bg-[#1E6BFF]/4 transition-colors">
                                        <td className="px-6 md:px-8 py-3 font-semibold text-[#0A1F44]">{r.year}</td>
                                        <td className="px-4 py-3 text-right tabular-nums">{fmtINRCompact(r.principal)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums text-[#0A1F44]/65">{fmtINRCompact(r.interest)}</td>
                                        <td className="px-4 py-3 text-right tabular-nums">{fmtINRCompact(r.total)}</td>
                                        <td className="px-6 md:px-8 py-3 text-right tabular-nums text-[#0A1F44]">{fmtINRCompact(r.balance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="border-t border-[#0A1F44]/8 px-6 md:px-8 py-3 flex items-center justify-between">
                        <span className="text-[11px] text-[#0A1F44]/55">
                            Showing {visible.length} of {fullSchedule.length} years
                        </span>
                        <button
                            type="button"
                            onClick={() => setExpanded((v) => !v)}
                            data-testid="loan-schedule-toggle"
                            className="text-xs font-semibold text-[#1E6BFF] hover:text-[#0A1F44] transition-colors"
                        >
                            {expanded ? "Collapse to first 5 years ↑" : "Expand to view full schedule ↓"}
                        </button>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Section J — Insights (retained)
// ─────────────────────────────────────────────────────────────────────
function Insights() {
    const lines = [
        "You only cross 25% ownership at year ~7 — most of the early years go to interest.",
        "A 50 bps lower rate saves ₹6.4 L; a ₹2 L annual prepayment cuts 4.6 yrs and saves ₹18 L.",
        "After tax savings, your effective EMI is closer to ₹57K — far closer to today's rent than the headline suggests.",
    ];
    return (
        <Reveal>
            <div data-testid="loan-insights" className="rounded-3xl border border-[#0A1F44]/10 bg-[#F7F8FB] p-6 md:p-8">
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold mb-2">— Levers worth pulling</p>
                <h3 className="font-display font-extrabold text-2xl text-[#0A1F44] mb-5">Insights</h3>
                <ul className="space-y-3">
                    {lines.map((l, i) => (
                        <li key={i} className="flex gap-3 text-[#0A1F44]">
                            <span className="text-[#1E6BFF] font-bold tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                            <span>{l}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────
export default function LoanRepaymentTab() {
    const { scrollTargetId, setScrollTargetId } = useHomeIQ();
    useEffect(() => {
        if (!scrollTargetId) return;
        // Allow the page to mount before scrolling.
        const t = setTimeout(() => {
            const el = document.getElementById(scrollTargetId);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            setScrollTargetId(null);
        }, 100);
        return () => clearTimeout(t);
    }, [scrollTargetId, setScrollTargetId]);
    return (
        <section data-testid="loan-tab" className="py-14 px-6 md:px-12 bg-white">
            <div className="max-w-7xl mx-auto space-y-10">
                <SectionHead
                    eyebrow="Loan repayment"
                    title="The 20-year truth, in one screen."
                    sub="Every EMI is two stories — what stays with the bank and what comes back as ownership."
                />
                <ContextStrip />
                <LoanInputs />
                <Summary />
                <EquityMilestones />
                <PrepaymentSimulator />
                <RateSensitivity />
                <BankComparison />
                <EffectiveEMIvsRent />
                <Schedule />
                <Insights />
            </div>
        </section>
    );
}
